import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import moment from 'moment';
import PropTypes from 'prop-types';
import DatePickerComponent from '../common/DatePickerComponent';

export const CUSTOM_SERVICE_TRANSACTION_TYPES = ['Debit', 'Credit'];
export const CUSTOM_SERVICE_CHARGE_TYPES = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Half Yearly', 'Yearly', '2 Years', '5 Years'];
const MAX_NAME_LENGTH = 100;

const blockInvalidNumberKeys = (evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault();

const buildInitialForm = (editItem) => {
    if (editItem) {
        return {
            serviceName: editItem.ServiceType || '',
            transactionType: editItem.TransactionType || CUSTOM_SERVICE_TRANSACTION_TYPES[0],
            chargeType: editItem.ChargeType || CUSTOM_SERVICE_CHARGE_TYPES[0],
            validFrom: null,
            validTo: null,
            amount: '',
            additionalCharge: 0,
        };
    }
    return {
        serviceName: '',
        transactionType: CUSTOM_SERVICE_TRANSACTION_TYPES[0],
        chargeType: CUSTOM_SERVICE_CHARGE_TYPES[0],
        validFrom: new Date(),
        validTo: null,
        amount: '',
        additionalCharge: 0,
    };
};

const validateRate = (form, errors) => {
    if (!form.validFrom) {
        errors.validFrom = 'Valid From is required.';
    }
    if (form.amount === '' || form.amount === null || form.amount === undefined) {
        errors.amount = 'Amount is required.';
    } else if (Number(form.amount) < 0) {
        errors.amount = 'Amount cannot be negative.';
    }
    if (Number(form.additionalCharge || 0) < 0) {
        errors.additionalCharge = 'Additional Charge cannot be negative.';
    }
    if (form.validFrom && form.validTo && moment(form.validTo).isBefore(form.validFrom)) {
        errors.validTo = 'Valid To cannot be earlier than Valid From.';
    }
};

export const validateCustomServiceForm = (form, isEdit) => {
    const errors = {};
    const name = (form.serviceName || '').trim();
    if (!name) {
        errors.serviceName = 'Service Type Name is required.';
    } else if (name.length > MAX_NAME_LENGTH) {
        errors.serviceName = `Service Type Name cannot exceed ${MAX_NAME_LENGTH} characters.`;
    }
    if (!isEdit) {
        validateRate(form, errors);
    }
    return errors;
};

const CustomServiceTypeModal = ({ show, editItem, isSaving, apiError, onClose, onSubmit }) => {
    const isEdit = !!editItem;
    const isBillingDefinitionLocked = isEdit && !!editItem.IsBillingDefinitionLocked;
    const [form, setForm] = useState(() => buildInitialForm(editItem));
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (show) {
            setForm(buildInitialForm(editItem));
            setErrors({});
        }
    }, [show, editItem]);

    const updateField = (name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleClose = () => {
        if (!isSaving) {
            onClose();
        }
    };

    const handleSubmit = () => {
        const validationErrors = validateCustomServiceForm(form, isEdit);
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length === 0) {
            onSubmit({ ...form, serviceName: form.serviceName.trim() });
        }
    };

    const title = isEdit ? 'Edit Custom Service Type' : 'Add New Custom Service Type';
    const submitLabel = isEdit ? 'Save Changes' : 'Add Custom Service Type';

    return (
        <Modal size="lg" show={show} onHide={handleClose} backdrop="static" keyboard={!isSaving} centered>
            <Modal.Header className="custom-service-modal-header">
                <h6 className="custom-service-modal-title">{title}</h6>
                <button type="button" className="btn-close" aria-label="Close" onClick={handleClose} disabled={isSaving}></button>
            </Modal.Header>
            <Modal.Body className="fontFix">
                {apiError && <div className="require-field mb-2">{apiError}</div>}
                <div className="row">
                    <div className="col-12 mb-3">
                        <label htmlFor="customServiceName">Service Type Name <span className="error">*</span></label>
                        <input
                            id="customServiceName"
                            type="text"
                            className="form-control"
                            placeholder="e.g. Pallet Storage Fee"
                            maxLength={MAX_NAME_LENGTH}
                            value={form.serviceName}
                            onChange={(e) => updateField('serviceName', e.target.value)}
                        />
                        <div className="require-field">{errors.serviceName}</div>
                    </div>
                    <div className="col-md-6 mb-3">
                        <label htmlFor="customTransactionType">Transaction Type <span className="error">*</span></label>
                        <select
                            id="customTransactionType"
                            className="form-control"
                            value={form.transactionType}
                            disabled={isBillingDefinitionLocked}
                            onChange={(e) => updateField('transactionType', e.target.value)}
                        >
                            {CUSTOM_SERVICE_TRANSACTION_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                        </select>
                    </div>
                    <div className="col-md-6 mb-3">
                        <label htmlFor="customChargeType">Charge Type <span className="error">*</span></label>
                        <select
                            id="customChargeType"
                            className="form-control"
                            value={form.chargeType}
                            disabled={isBillingDefinitionLocked}
                            onChange={(e) => updateField('chargeType', e.target.value)}
                        >
                            {CUSTOM_SERVICE_CHARGE_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                        </select>
                    </div>
                    {!isEdit && (
                        <>
                            <div className="col-md-4 mb-3">
                                <label htmlFor="customValidFrom">Valid From <span className="error">*</span></label>
                                <DatePickerComponent
                                    dateField={form.validFrom}
                                    handleDateChange={(date) => updateField('validFrom', date)}
                                    fieldName="validFrom"
                                    dateFormat="MM-dd-yyyy, h:mm aa"
                                    timeSelect={true}
                                />
                                <div className="require-field">{errors.validFrom}</div>
                            </div>
                            <div className="col-md-4 mb-3">
                                <label htmlFor="customValidTo">Valid To</label>
                                <DatePickerComponent
                                    dateField={form.validTo}
                                    handleDateChange={(date) => updateField('validTo', date)}
                                    fieldName="validTo"
                                    dateFormat="MM-dd-yyyy, h:mm aa"
                                    timeSelect={true}
                                />
                                <div className="require-field">{errors.validTo}</div>
                            </div>
                            <div className="col-md-4 mb-3">
                                <label htmlFor="customAmount">Amount (Charge/Unit) $ <span className="error">*</span></label>
                                <input
                                    id="customAmount"
                                    type="number"
                                    min={0}
                                    className="form-control"
                                    placeholder="0.00"
                                    value={form.amount}
                                    onChange={(e) => updateField('amount', e.target.value)}
                                    onKeyDown={blockInvalidNumberKeys}
                                    onWheel={(e) => e.target.blur()}
                                />
                                <div className="require-field">{errors.amount}</div>
                            </div>
                            <div className="col-md-4 mb-3">
                                <label htmlFor="customAdditionalCharge">Additional Charge $</label>
                                <input
                                    id="customAdditionalCharge"
                                    type="number"
                                    min={0}
                                    className="form-control"
                                    value={form.additionalCharge}
                                    onChange={(e) => updateField('additionalCharge', e.target.value)}
                                    onKeyDown={blockInvalidNumberKeys}
                                    onWheel={(e) => e.target.blur()}
                                />
                                <div className="require-field">{errors.additionalCharge}</div>
                            </div>
                        </>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn cus-primary-transparent-btn" onClick={handleClose} disabled={isSaving}>
                    Cancel
                </button>
                <button type="button" className="btn cus-seconday-bg-btn marginLeft5" onClick={handleSubmit} disabled={isSaving}>
                    {isSaving ? 'Saving...' : submitLabel}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

CustomServiceTypeModal.propTypes = {
    show: PropTypes.bool.isRequired,
    editItem: PropTypes.object,
    isSaving: PropTypes.bool,
    apiError: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
};

export default CustomServiceTypeModal;
